import React, { useState } from 'react';
import { commentsAPI } from '../../services/api';
import CommentItem from './CommentItem';

const ReplyList = ({ parentComment, initialReplies = [] }) => {
  const [replies, setReplies] = useState(initialReplies);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(parentComment.replies_count > initialReplies.length);
  const [cursor, setCursor] = useState(null);

  const loadReplies = async () => {
    if (isLoading) return;

    setIsLoading(true);
    try {
      const params = { per_page: 20 };
      if (cursor) {
        params.cursor = cursor;
      }

      const response = await commentsAPI.getReplies(parentComment.id, params);
      const newReplies = response.data.replies;

      setReplies(prev => cursor ? [...prev, ...newReplies] : newReplies);
      setCursor(response.data.next_cursor);
      setHasMore(response.data.has_more);
    } catch (error) {
      console.error('Error loading replies:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExpand = () => {
    if (!isExpanded && replies.length === 0) {
      loadReplies();
    }
    setIsExpanded(!isExpanded);
  };

  const handleReplyUpdate = (updatedReply) => {
    setReplies(prev =>
      prev.map(reply => reply.id === updatedReply.id ? updatedReply : reply)
    );
  };

  const handleReplyDelete = (replyId) => {
    setReplies(prev => prev.filter(reply => reply.id !== replyId));
    // Update parent comment's reply count
    parentComment.replies_count = Math.max(0, parentComment.replies_count - 1);
  };

  const handleNewReply = (newReply) => {
    setReplies(prev => [newReply, ...prev]);
    setIsExpanded(true);
    // Update parent comment's reply count
    parentComment.replies_count += 1;
  };

  if (parentComment.replies_count === 0) {
    return null;
  }

  return (
    <div className="ml-14 mt-3 space-y-3">
      {/* Expand/Collapse Button */}
      <button
        onClick={handleExpand}
        className="flex items-center space-x-2 text-army-light hover:text-brand-yellow font-medium text-sm transition-colors"
      >
        <svg
          className={`w-4 h-4 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
        <span>
          {isExpanded ? 'Hide' : 'Show'} {parentComment.replies_count}{' '}
          {parentComment.replies_count === 1 ? 'reply' : 'replies'}
        </span>
      </button>

      {/* Replies */}
      {isExpanded && (
        <div className="space-y-3 border-l-2 border-army/25 pl-4">
          {replies.map(reply => (
            <CommentItem
              key={reply.id}
              comment={reply}
              onUpdate={handleReplyUpdate}
              onDelete={handleReplyDelete}
              onReply={handleNewReply}
              showReplyButton={false}
            />
          ))}

          {/* Load More Button */}
          {hasMore && (
            <button
              onClick={loadReplies}
              disabled={isLoading}
              className="text-army-light hover:text-brand-yellow font-medium text-sm transition-colors disabled:opacity-50"
            >
              {isLoading ? 'Loading...' : 'Load more replies'}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ReplyList;
